import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import Chip from "@mui/material/Chip";

export default function Logger() {
  const { profile } = useAuth();
  const [students, setStudents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    studentId: "",
    hours: "",
    date: new Date().toISOString().split("T")[0],
  });

  // Fetch tutor data only once, only again when profile.id changes
  const fetchTutorData = useCallback(async () => {
    if (!profile?.id) return;

    setLoading(true);
    try {
      // Fetch assigned students and their hours bought
      const { data: assignments, error } = await supabase
        .from("student_tutor_assignments")
        .select(
          `student_id,
           rate,
           students (full_name)`,
        )
        .eq("tutor_id", profile.id);

      if (error) {
        console.error("Error fetching assignments:", error);
      } else {
        setStudents(assignments || []);
      }
      // Fetch recent logs with student names
      const { data: lessonLogs, error: logError } = await supabase
        .from("lesson_logs")
        .select(
          ` id,
            date,
            hours,
            status,
            student:students(full_name)
          `,
        )
        .eq("tutor_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(5);

      if (!logError) {
        setLogs(lessonLogs || []);
      }
    } catch (logError) {
      console.error("Error fetching lesson logs:", logError);
    } finally {
      setLoading(false);
    }
  }, [profile.id]);

  useEffect(() => {
    fetchTutorData();
  }, [fetchTutorData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newSessionHours = parseFloat(formData.hours);

    try {
      const [assignmentRes, usageRes] = await Promise.all([
        // Get student budget and their can_exceed rule
        supabase
          .from("student_tutor_assignments")
          .select("hours_bought, students(can_exceed)")
          .eq("student_id", formData.studentId)
          .eq("tutor_id", profile?.id)
          .single(),

        // Get pending and approved sessions for this student-tutor pair
        supabase
          .from("lesson_logs")
          .select("hours")
          .eq("student_id", formData.studentId)
          .eq("tutor_id", profile?.id)
          .in("status", ["approved", "pending"]),
      ]);

      if (assignmentRes.error)
        throw new Error("No assignment found for this pair.");

      const hoursBought = assignmentRes.data.hours_bought;
      const canExceed = assignmentRes.data.students.can_exceed;

      const currentUsage =
        usageRes.data?.reduce(
          (acc, log) => acc + (Number(log.hours) || 0),
          0,
        ) || 0;

      // Validation check
      if (!canExceed && currentUsage + newSessionHours > hoursBought) {
        alert(
          `Validation Error: This student has a budget of ${hoursBought} hours. ` +
            `They have already used ${currentUsage} hours. ` +
            `Submitting this would total ${currentUsage + newSessionHours} hours.`,
        );
        setLoading(false);
        return;
      }

      const sessionRate =
        students.find((s) => s.student_id === formData.studentId).rate || null;

      // If validation passes, insert the log
      const { error: insertError } = await supabase.from("lesson_logs").insert([
        {
          tutor_id: profile.id,
          student_id: formData.studentId,
          hours: newSessionHours,
          date: formData.date,
          rate: sessionRate,
        },
      ]);

      if (insertError) throw insertError;

      // Success
      setFormData({ ...formData, hours: "" });
      fetchTutorData();
      alert("Session logged successfully!");
    } catch (error) {
      console.error("Submission Error:", error.message);
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // -------------------- Render -------------------- //

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        color="primary"
        sx={{ mb: 4, fontWeight: "bold" }}
      >
        Welcome, {profile?.full_name.split(" ")[0]}
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* LOG HOURS FORM */}
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Log New Session
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TextField
                    select
                    fullWidth
                    label="Select Student"
                    value={formData.studentId}
                    onChange={(e) =>
                      setFormData({ ...formData, studentId: e.target.value })
                    }
                    sx={{ mb: 2 }}
                    required
                  >
                    {students.length === 0 ? (
                      <MenuItem disabled>No students assigned yet</MenuItem>
                    ) : (
                      students.map((s) => (
                        <MenuItem key={s.student_id} value={s.student_id}>
                          {s.students?.full_name}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                  <TextField
                    fullWidth
                    type="number"
                    label="Hours"
                    slotProps={{ htmlInput: { step: 0.25, min: 0, max: 4 } }}
                    value={formData.hours}
                    onChange={(e) =>
                      setFormData({ ...formData, hours: e.target.value })
                    }
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);

                      if (val > 4) {
                        setFormData({ ...formData, hours: "4" });
                      } else if (!isNaN(val)) {
                        setFormData({
                          ...formData,
                          hours: Math.round(val * 4) / 4,
                        });
                      }
                    }}
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    sx={{ mb: 3 }}
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Hours"}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* RECENT LOGS TABLE */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Recent Sessions
            </Typography>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : logs.length === 0 ? (
              <Alert severity="info">No sessions logged yet.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {new Date(log.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {log.student?.full_name || "Deleted Student"}
                      </TableCell>
                      <TableCell>
                        {log.adjusted_hours ? (
                          <Box component="span">
                            <strike style={{ color: "gray", marginRight: 4 }}>
                              {log.hours}
                            </strike>
                            <strong>{log.adjusted_hours}</strong>
                          </Box>
                        ) : (
                          log.hours
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={
                            log.status === "approved" ? "success" : "warning"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
